/** Phase 3B.1.2 — Landing Pages list + detail.
 *
 * - List route: `/app/landing-pages` (`LandingPagesList`)
 * - Detail route: `/app/landing-pages/:id` (`LandingPageDetail`)
 *
 * Both views talk to the LP Agent + `/landing-pages` REST surface that
 * Phase 3B.0.3 + 3B.1.2 ship. Manager+ can create drafts, attach hero
 * images, and view history; analyst is view-only.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { showAgentError } from "../../components/app/AgentErrorToast";
import { AgentGenerationSpinner } from "../../components/app/AgentGenerationSpinner";
import { api, auth } from "../../lib/api";
import { useLocale, useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../../components/ui/accordion";
import { ImageIcon, Link as LinkIcon, ArrowLeft, FileText, Sparkles, GitBranch, CheckCircle2, XCircle, MinusCircle, CircleDot } from "lucide-react";
import { SkippedReason, AttemptBadge } from "../../components/app/WorkflowStepDetails";

const FRAMEWORKS = ["AIDA", "PAS", "BAB", "Authority", "SocialProof"];
const WRITE_ROLES = new Set(["owner", "admin", "manager"]);
const PUBLISH_ROLES = new Set(["owner", "admin", "manager"]);
const IMAGE_ROLES = new Set(["owner", "admin", "manager", "producer"]);

function StatusBadge({ lp }) {
  const status = lp.published_at ? "published"
    : lp.compliance_check_status === "failed" ? "compliance_failed"
    : "draft";
  const tone = status === "published" ? "bg-emerald-100 text-emerald-900"
    : status === "compliance_failed" ? "bg-rose-100 text-rose-900"
    : "bg-stone-100 text-stone-900";
  const label = status === "published" ? "Published"
    : status === "compliance_failed" ? "Compliance failed"
    : "Draft";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
            data-testid={`lp-status-${lp.id}`}>
      {label}
    </span>
  );
}

async function lpPublish(id, public_slug) {
  return (await api.post(`/landing-pages/${id}/publish`,
                                    { public_slug })).data;
}

async function lpUnpublish(id) {
  return (await api.post(`/landing-pages/${id}/unpublish`)).data;
}

function PublicUrlCell({ lp, tenantSlug }) {
  const url = lp.published_at && lp.public_slug
    ? `/api/v1/lp/${tenantSlug}/${lp.public_slug}` : null;
  if (!url) return null;
  return (
    <div className="flex items-center gap-2">
      <a href={url} target="_blank" rel="noopener noreferrer"
          className="text-xs text-emerald-700 underline truncate max-w-[280px]"
          data-testid={`lp-public-url-${lp.id}`}>{url}</a>
      <Button variant="ghost" size="sm"
              data-testid={`lp-copy-url-${lp.id}`}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("URL copied");
                } catch (e) { toast.error("Copy failed"); }
              }}>Copy</Button>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const d = Math.floor((Date.now() - t) / 1000);
  if (d < 60) return `${d}s`;
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

function ConfidenceChip({ value }) {
  const v = typeof value === "number" ? value : 0;
  const tone = v >= 0.8 ? "bg-emerald-100 text-emerald-900" :
                v >= 0.6 ? "bg-amber-100 text-amber-900" : "bg-rose-100 text-rose-900";
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-mono ${tone}`}>{v.toFixed(2)}</span>;
}

function AIRunBadgeInline({ model, cost, latency_ms, confidence }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono" data-testid="lp-run-badge">
      <span>{model || "—"}</span><span>·</span>
      <span>${typeof cost === "number" ? cost.toFixed(4) : "—"}</span><span>·</span>
      <span>{latency_ms ? `${(latency_ms / 1000).toFixed(1)}s` : "—"}</span><span>·</span>
      <span>Q {typeof confidence === "number" ? confidence.toFixed(2) : "—"}</span>
    </div>
  );
}

// ----- List view --------------------------------------------------------

export function LandingPagesList() {
  const t = useT();
  const role = (auth.getUser() || {}).role || "";
  const canWrite = WRITE_ROLES.has(role);
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const nav = useNavigate();

  async function loadList() {
    try {
      const r = await api.get("/landing-pages");
      setItems(r.data.items || []);
    } catch (e) { /* noop */ }
  }
  useEffect(() => {
    loadList();
    api.get("/campaigns").then((r) => setCampaigns((r.data.items || []).slice(0, 50))).catch(() => {});
    api.get("/brand-profile").then((r) => setPersonas(r.data.target_groups || [])).catch(() => {});
  }, []);

  const campaignsById = useMemo(() => {
    const m = {};
    for (const c of campaigns) m[c.id] = c;
    return m;
  }, [campaigns]);

  return (
    <div className="space-y-4" data-testid="landing-pages-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("landing_pages.list_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("landing_pages.subtitle")}</p>
        </div>
        {canWrite && (
          <Button data-testid="lp-new-draft-btn" onClick={() => setModalOpen(true)}>
            {t("landing_pages.new_draft_button")}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <Card data-testid="lp-empty">
          <CardContent className="pt-6 pb-6 text-sm text-muted-foreground">
            {t("landing_pages.empty_state")}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y" data-testid="lp-list">
              {items.map((lp) => {
                const camp = lp.campaign_id ? campaignsById[lp.campaign_id] : null;
                const title = lp.hero?.headline || lp.summary || `LP · ${(lp.id || "").slice(0, 8)}`;
                const hasImage = (lp.attached_image_asset_ids || []).length > 0;
                return (
                  <li key={lp.id} className="px-4 py-3 flex items-center gap-3" data-testid={`lp-row-${lp.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate" data-testid={`lp-row-title-${lp.id}`}>{title}</span>
                        <StatusBadge lp={lp} />
                        <Badge variant="outline" className="font-mono text-[11px]" data-testid={`lp-row-framework-${lp.id}`}>
                          {lp.framework || "AIDA"}
                        </Badge>
                        {hasImage && (
                          <Badge variant="secondary" className="gap-1 text-[11px]" data-testid={`lp-row-has-image-${lp.id}`}>
                            <ImageIcon className="size-3" /> {t("landing_pages.has_hero_image")}
                          </Badge>
                        )}
                        {camp && (
                          <Link to={`/app/campaigns/${camp.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline" data-testid={`lp-row-campaign-${lp.id}`}>
                            <LinkIcon className="size-3" /> {camp.name || t("landing_pages.linked_campaign")}
                          </Link>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <span>{t("landing_pages.updated")} {relativeTime(lp.updated_at || lp.created_at)}</span>
                        <span>·</span>
                        <ConfidenceChip value={lp.confidence} />
                      </div>
                      <PublicUrlCell lp={lp} tenantSlug={(auth.getUser() || {}).slug || "brimo"} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => nav(`/app/landing-pages/${lp.id}`)}
                            data-testid={`lp-row-open-${lp.id}`}>
                      {t("landing_pages.view")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      <NewDraftModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        campaigns={campaigns}
        personas={personas}
        onCreated={(lpid) => { setModalOpen(false); nav(`/app/landing-pages/${lpid}`); }}
      />
    </div>
  );
}

function NewDraftModal({ open, onOpenChange, campaigns, personas, onCreated }) {
  const t = useT();
  const { locale } = useLocale();
  const [mode, setMode] = useState("brief");
  const [campaignId, setCampaignId] = useState("");
  const [brief, setBrief] = useState("");
  const [personaId, setPersonaId] = useState("");
  const [framework, setFramework] = useState("AIDA");
  const [draftLocale, setDraftLocale] = useState(locale || "de");
  const [autoHero, setAutoHero] = useState(true);  // Phase 4.2.0
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function submit() {
    setError(null);
    if (mode === "brief" && !brief.trim()) {
      setError(t("landing_pages.modal.validation_required")); return;
    }
    if (mode === "campaign" && !campaignId) {
      setError(t("landing_pages.modal.validation_required")); return;
    }
    setSubmitting(true);
    try {
      const body = {
        framework, locale: draftLocale,
        target_persona_id: personaId || undefined,
      };
      if (mode === "campaign") body.campaign_id = campaignId;
      else body.brief = brief;
      const endpoint = autoHero ? "/ai/landing-page/draft-with-hero" : "/ai/landing-page/draft";
      const r = await api.post(endpoint, body);
      if (autoHero && r.data?.attached === false) {
        // Honest: surface the warning; LP draft is still valuable.
        toast.warning(t("landing_pages.modal.hero_failed_warning"));
      }
      onCreated(r.data.landing_page_id);
    } catch (e) {
      showAgentError({ err: e, locale: (t("common.locale_code") || "de") });
      const msg = e?.response?.data?.detail?.error;
      setError(msg ? `${t("landing_pages.modal.error_generic")} (${msg})` : t("landing_pages.modal.error_generic"));
    } finally { setSubmitting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lp-new-draft-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("landing_pages.modal.title")}</DialogTitle>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="campaign" data-testid="lp-mode-campaign">{t("landing_pages.modal.mode_campaign")}</TabsTrigger>
            <TabsTrigger value="brief" data-testid="lp-mode-brief">{t("landing_pages.modal.mode_brief")}</TabsTrigger>
          </TabsList>
          <TabsContent value="campaign" className="pt-3 space-y-2">
            <label className="text-xs text-muted-foreground">{t("landing_pages.modal.campaign_label")}</label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger data-testid="lp-campaign-select"><SelectValue placeholder={t("landing_pages.modal.campaign_placeholder")} /></SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`lp-campaign-opt-${c.id}`}>
                    {c.name || c.id.slice(0, 8)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TabsContent>
          <TabsContent value="brief" className="pt-3 space-y-2">
            <label className="text-xs text-muted-foreground">{t("landing_pages.modal.brief_label")}</label>
            <Textarea
              data-testid="lp-brief-input"
              rows={5}
              value={brief}
              placeholder={t("landing_pages.modal.brief_placeholder")}
              onChange={(e) => setBrief(e.target.value)}
            />
          </TabsContent>
        </Tabs>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">{t("landing_pages.modal.persona_label")}</label>
            <Select value={personaId} onValueChange={setPersonaId}>
              <SelectTrigger data-testid="lp-persona-select"><SelectValue placeholder={t("landing_pages.modal.persona_placeholder")} /></SelectTrigger>
              <SelectContent>
                {personas.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`lp-persona-opt-${p.id}`}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">{t("landing_pages.modal.framework_label")}</label>
            <Select value={framework} onValueChange={setFramework}>
              <SelectTrigger data-testid="lp-framework-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FRAMEWORKS.map((f) => (
                  <SelectItem key={f} value={f} data-testid={`lp-framework-opt-${f}`}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">{t("landing_pages.modal.locale_label")}</label>
            <Select value={draftLocale} onValueChange={setDraftLocale}>
              <SelectTrigger data-testid="lp-locale-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="de">Deutsch</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {error && <p className="text-sm text-rose-600" data-testid="lp-modal-error">{error}</p>}
        {submitting && (
          <AgentGenerationSpinner active locale={draftLocale === "en" ? "en" : "de"} />
        )}
        <label className="flex items-center gap-2 text-xs"
          data-testid="lp-modal-auto-hero-label">
          <input
            type="checkbox"
            data-testid="lp-modal-auto-hero"
            checked={autoHero}
            onChange={(e) => setAutoHero(e.target.checked)}
          />
          <span>{t("landing_pages.modal.auto_hero")}</span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lp-modal-cancel">
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="lp-modal-submit">
            {submitting ? t("landing_pages.modal.submitting") : t("landing_pages.modal.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----- Detail view ------------------------------------------------------

function useAuthedBlob(assetId) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke = null;
    if (!assetId) return undefined;
    api.get(`/assets/${assetId}/file`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [assetId]);
  return src;
}

function HeroImageThumb({ assetId }) {
  const src = useAuthedBlob(assetId);
  if (!src) return (
    <div data-testid={`lp-thumb-${assetId}`} className="w-24 h-24 rounded-md border bg-muted flex items-center justify-center text-xs text-muted-foreground">
      <ImageIcon className="size-4" />
    </div>
  );
  return (
    <Link to={`/app/assets`} className="block">
      <img src={src} alt="" data-testid={`lp-thumb-${assetId}`}
            className="w-24 h-24 rounded-md border object-cover" />
    </Link>
  );
}

export function LandingPageDetail() {
  const t = useT();
  const { locale } = useLocale();
  const role = (auth.getUser() || {}).role || "";
  const canGenImage = IMAGE_ROLES.has(role);
  const canWriteLP = WRITE_ROLES.has(role);
  const { id } = useParams();
  const nav = useNavigate();
  const [lp, setLp] = useState(null);
  const [history, setHistory] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [lastImageAsset, setLastImageAsset] = useState(null);

  async function loadDetail() {
    try {
      const r = await api.get(`/landing-pages/${id}`);
      setLp(r.data);
      if (r.data.campaign_id) {
        api.get(`/campaigns/${r.data.campaign_id}`)
          .then((cc) => setCampaign(cc.data))
          .catch(() => {});
      }
    } catch (e) { /* noop */ }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDetail(); }, [id]);

  async function generateHeroImage() {
    if (!lp?.hero?.hero_image_prompt) {
      toast.error(t("landing_pages.hero_image_failed")); return;
    }
    setGenerating(true);
    try {
      const body = {
        prompt: lp.hero.hero_image_prompt,
        aspect_ratio: "16:9", n_images: 1,
        platform: "landing_page", locale,
      };
      if (lp.source_campaign_id || lp.campaign_id) body.campaign_id = lp.campaign_id;
      const r = await api.post("/ai/creative/images", body);
      const asset = (r.data.assets || [])[0];
      if (!asset) { toast.error(t("landing_pages.hero_image_failed")); return; }
      setLastImageAsset(asset);
      if (canWriteLP) {
        try {
          const patched = await api.patch(`/landing-pages/${id}`, {
            attached_image_asset_ids: [asset.id],
            change_summary: "attach_image_asset",
          });
          setLp(patched.data);
          toast.success(t("landing_pages.attached_toast"));
        } catch (e) {
          toast.error(t("landing_pages.attach_failed"));
        }
      } else {
        toast.success(t("landing_pages.saved_to_library_toast"));
      }
    } catch (e) {
      toast.error(t("landing_pages.hero_image_failed"));
    } finally { setGenerating(false); }
  }

  if (!lp) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="lp-detail-loading">{t("common.loading")}</div>;
  }

  const title = lp.hero?.headline || `LP · ${(lp.id || "").slice(0, 8)}`;
  const valueProps = lp.value_props || [];
  const quotes = (lp.social_proof_section?.quote_examples) || [];
  const stats = (lp.social_proof_section?.stat_examples) || [];
  const faq = lp.faq || [];
  const legal = lp.legal_blocks || {};
  const attachedIds = lp.attached_image_asset_ids || [];

  return (
    <div className="space-y-4" data-testid="lp-detail">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => nav("/app/landing-pages")}
                    data-testid="lp-back-btn"><ArrowLeft className="size-4 mr-1" />
              {t("landing_pages.back_to_list")}</Button>
            <Badge variant="outline" className="font-mono text-[11px]">{lp.framework || "AIDA"}</Badge>
            {campaign && (
              <Link to={`/app/campaigns/${campaign.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline" data-testid="lp-detail-campaign-link">
                <LinkIcon className="size-3" /> {campaign.name}
              </Link>
            )}
            <ConfidenceChip value={lp.confidence} />
          </div>
          <h1 className="text-2xl font-semibold" data-testid="lp-detail-title">{title}</h1>
        </div>
        <AIRunBadgeInline model={lp.source_model || lp.model || ""} cost={lp.source_cost_usd}
                          latency_ms={lp.source_latency_ms} confidence={lp.confidence} />
      </div>

      {/* Hero card */}
      <Card data-testid="lp-hero-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t("landing_pages.detail.hero")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-lg font-semibold" data-testid="lp-hero-headline">{lp.hero?.headline || "—"}</p>
            <p className="text-sm text-muted-foreground" data-testid="lp-hero-subheadline">{lp.hero?.subheadline || ""}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">{t("landing_pages.detail.cta_primary")}</div>
              <div className="font-medium" data-testid="lp-hero-cta-primary">{lp.hero?.cta_primary || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("landing_pages.detail.cta_secondary")}</div>
              <div className="font-medium" data-testid="lp-hero-cta-secondary">{lp.hero?.cta_secondary || "—"}</div>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t("landing_pages.detail.hero_image_prompt")}</div>
            <pre data-testid="lp-hero-image-prompt"
                  className="bg-muted rounded-md p-2 text-xs whitespace-pre-wrap font-mono select-text">
{lp.hero?.hero_image_prompt || ""}
            </pre>
          </div>
          {canGenImage && (
            <Button onClick={generateHeroImage} disabled={generating || !lp.hero?.hero_image_prompt}
                    data-testid="lp-generate-hero-image-btn"
                    className="gap-2">
              <Sparkles className="size-4" />
              {generating ? t("landing_pages.generating_hero_image") : t("landing_pages.generate_hero_image")}
            </Button>
          )}
          {(attachedIds.length > 0 || lastImageAsset) && (
            <div className="flex items-center gap-3 flex-wrap pt-1" data-testid="lp-hero-image-row">
              {attachedIds.map((aid) => <HeroImageThumb key={aid} assetId={aid} />)}
              {lastImageAsset && !attachedIds.includes(lastImageAsset.id) && (
                <div className="flex items-center gap-2 text-xs">
                  <HeroImageThumb assetId={lastImageAsset.id} />
                  <Link to={`/app/assets`} className="underline text-muted-foreground"
                        data-testid="lp-view-in-assets-link">
                    {t("landing_pages.view_in_assets")}
                  </Link>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Value props */}
      <Card data-testid="lp-value-props-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("landing_pages.detail.value_props")}</CardTitle></CardHeader>
        <CardContent>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="lp-value-props">
            {valueProps.map((v, i) => (
              <li key={i} className="rounded-md border p-3 space-y-1" data-testid={`lp-value-prop-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{v.title}</span>
                  {v.icon_hint && <Badge variant="secondary" className="text-[10px]">{v.icon_hint}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{v.description}</p>
              </li>
            ))}
            {valueProps.length === 0 && <li className="text-sm text-muted-foreground">—</li>}
          </ul>
        </CardContent>
      </Card>

      {/* Social proof */}
      <Card data-testid="lp-social-proof-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("landing_pages.detail.social_proof")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t("landing_pages.detail.quotes")}</div>
            <ul className="space-y-2">
              {quotes.map((q, i) => (
                <li key={i} className="border-l-2 pl-3 italic text-sm" data-testid={`lp-quote-${i}`}>{q}</li>
              ))}
              {quotes.length === 0 && <li className="text-sm text-muted-foreground">—</li>}
            </ul>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t("landing_pages.detail.stats")}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {stats.map((s, i) => (
                <div key={i} className="rounded-md border p-2 text-sm font-medium" data-testid={`lp-stat-${i}`}>{s}</div>
              ))}
              {stats.length === 0 && <span className="text-sm text-muted-foreground">—</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQ accordion */}
      <Card data-testid="lp-faq-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("landing_pages.detail.faq")}</CardTitle></CardHeader>
        <CardContent>
          {faq.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
            <Accordion type="single" collapsible className="w-full" data-testid="lp-faq-accordion">
              {faq.map((f, i) => (
                <AccordionItem key={i} value={`q-${i}`} data-testid={`lp-faq-item-${i}`}>
                  <AccordionTrigger className="text-sm">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Legal blocks */}
      <Card data-testid="lp-legal-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("landing_pages.detail.legal")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t("landing_pages.detail.gdpr_consent")}</div>
            <blockquote data-testid="lp-gdpr-consent" className="border-l-4 border-amber-400 bg-amber-50/40 dark:bg-amber-900/10 p-2 text-sm whitespace-pre-wrap">
              {legal.gdpr_consent_text || "—"}
            </blockquote>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">{t("landing_pages.detail.imprint_ref")}</div>
              <div className="font-mono text-xs" data-testid="lp-imprint-ref">{legal.imprint_ref || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("landing_pages.detail.privacy_ref")}</div>
              <div className="font-mono text-xs" data-testid="lp-privacy-ref">{legal.privacy_ref || "—"}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card data-testid="lp-history-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("landing_pages.detail.history")}</CardTitle></CardHeader>
        <CardContent>
          <HistoryList lpId={id} currentVersion={lp.version || 1} />
        </CardContent>
      </Card>

      {/* Phase 4.2.1 — Workflow timeline (only when the LP was created via the
          formal LP→Creative chain). */}
      {lp.workflow_instance_id && (
        <WorkflowTimelineCard instanceId={lp.workflow_instance_id} />
      )}
    </div>
  );
}

function HistoryList({ lpId, currentVersion }) {
  // Pulls history rows via a tiny client-side helper; backend exposes the
  // current version + previous snapshots via the `landing_pages_history`
  // collection (written automatically by the PATCH endpoint).
  const t = useT();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/landing-pages/${lpId}/history`).then((r) => {
      if (alive) setRows(r.data.items || []);
    }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [lpId]);

  if (rows === null) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  // History list is supplemental — show "no previous" if empty (only current version exists).
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground" data-testid="lp-history-empty">
      {t("landing_pages.detail.history_empty")} ({t("landing_pages.detail.history")} v{currentVersion})
    </p>;
  }
  return (
    <ul className="divide-y" data-testid="lp-history-list">
      {rows.map((h, i) => (
        <li key={i} className="py-2 text-sm flex items-center justify-between" data-testid={`lp-history-row-${i}`}>
          <span className="font-mono text-xs">v{h.version}</span>
          <span className="text-xs text-muted-foreground">{(h.updated_at || h.created_at || "").slice(0, 19).replace("T", " ")}</span>
          <span className="text-xs">{h.change_summary || "—"}</span>
        </li>
      ))}
    </ul>
  );
}

// Phase 4.2.1 — Workflow Timeline card. Renders a vertical stepper for the LP's
// originating workflow instance with clickable agent_run_id chips that route to
// the Agent Run Inspector. Only mounted when `lp.workflow_instance_id` exists.
function WorkflowTimelineCard({ instanceId }) {
  const t = useT();
  const [inst, setInst] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/workflows/instances/${instanceId}`).then((r) => {
      if (alive) setInst(r.data);
    }).catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [instanceId]);

  const STATUS_ICON = {
    succeeded: <CheckCircle2 className="size-4 text-emerald-600" />,
    failed: <XCircle className="size-4 text-rose-600" />,
    failed_continue: <XCircle className="size-4 text-amber-600" />,
    skipped: <MinusCircle className="size-4 text-muted-foreground" />,
    running: <CircleDot className="size-4 text-blue-600 animate-pulse" />,
  };
  const STATUS_TONE = {
    succeeded: "bg-emerald-100 text-emerald-900",
    failed: "bg-rose-100 text-rose-900",
    failed_continue: "bg-amber-100 text-amber-900",
    skipped: "bg-muted text-muted-foreground",
    running: "bg-blue-100 text-blue-900",
  };

  if (error) {
    return (
      <Card data-testid="lp-workflow-card">
        <CardHeader className="pb-2 flex flex-row items-center gap-2">
          <GitBranch className="size-4" />
          <CardTitle className="text-sm">{t("landing_pages.workflow.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground" data-testid="lp-workflow-error">
            {t("landing_pages.workflow.empty")}
          </p>
        </CardContent>
      </Card>
    );
  }
  if (!inst) {
    return (
      <Card data-testid="lp-workflow-card">
        <CardHeader className="pb-2 flex flex-row items-center gap-2">
          <GitBranch className="size-4" />
          <CardTitle className="text-sm">{t("landing_pages.workflow.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground" data-testid="lp-workflow-loading">{t("common.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  const stepRuns = inst.step_runs || [];

  return (
    <Card data-testid="lp-workflow-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <GitBranch className="size-4" />
          <CardTitle className="text-sm">{t("landing_pages.workflow.title")}</CardTitle>
          <Badge variant="outline" className="font-mono text-[10px] ml-auto"
                 data-testid="lp-workflow-instance-badge">
            {(inst.definition_key || "").slice(0, 40)}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{t("landing_pages.workflow.subtitle")}</p>
      </CardHeader>
      <CardContent>
        <ol className="relative border-l-2 border-muted pl-5 space-y-3 ml-1"
             data-testid="lp-workflow-timeline">
          {stepRuns.map((s, i) => {
            const label = t(`landing_pages.workflow.steps.${s.name}`) ||
                          s.name;
            const status = s.status || "running";
            const statusLabel = t(`landing_pages.workflow.status.${status}`) || status;
            return (
              <li key={i} className="relative" data-testid={`lp-workflow-step-${s.name}`}>
                <span className="absolute -left-[27px] top-0.5 inline-flex size-5 items-center justify-center rounded-full bg-background ring-2 ring-muted"
                      data-testid={`lp-workflow-step-${s.name}-icon`}>
                  {STATUS_ICON[status] || STATUS_ICON.running}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium" data-testid={`lp-workflow-step-${s.name}-label`}>{label}</span>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono ${STATUS_TONE[status] || ""}`}
                        data-testid={`lp-workflow-step-${s.name}-status`}>
                    {statusLabel}
                  </span>
                  {s.agent_run_id && (
                    <Link to={`/app/agents/runs/${s.agent_run_id}`}
                          data-testid={`lp-workflow-step-${s.name}-run-chip`}
                          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 hover:bg-indigo-100 text-indigo-900 px-2 py-0.5 text-[10px] font-mono ring-1 ring-indigo-200">
                      <Sparkles className="size-3" />
                      {(s.agent_run_id || "").slice(0, 8)}…
                    </Link>
                  )}
                  {s.type === "connector_action" && s.output?.lp_id && (
                    <Badge variant="secondary" className="text-[10px] font-mono">
                      lp:{String(s.output.lp_id).slice(0, 6)}…
                    </Badge>
                  )}
                    {s.reason && (
                    <SkippedReason step={s} testid={`lp-workflow-step-${s.name}`} />
                  )}
                  {(s.attempts && s.attempts.length > 1) && (
                    <AttemptBadge attempts={s.attempts}
                                  status={status}
                                  testid={`lp-workflow-step-${s.name}-attempts`} />
                  )}
                </div>
                {s.error && (
                  <p className="text-[11px] text-rose-700 mt-1 font-mono"
                     data-testid={`lp-workflow-step-${s.name}-error`}>{s.error}</p>
                )}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

// Default export = list view (so route `/app/landing-pages` lands here).
export default function LandingPages() {
  return <LandingPagesList />;
}

// Helper unused-import sentinel
void FileText;
void Input;
